import React, { useEffect, useState } from "react";
import { X, UserCheck } from "lucide-react";
import toast from "react-hot-toast";
import { reassignIgnoredLead } from "../../../api/lead";

/**
 * Re-assign an ignored lead to another agent (super-admin only).
 *
 * Michael Piccoli, Monday 12984261525 (revision): after a lead is ignored,
 * Christian needs to hand it to an agent to work. Re-assigning un-ignores the
 * lead server-side and drops it back into the new agent's pipeline; the new
 * agent gets the standard "lead assigned to you" alert.
 *
 * `agents` is [{ id, name }]. `onReassigned(lead)` fires after a successful
 * re-assign so the caller can drop the row / refresh.
 */
export default function ReassignIgnoredLeadModal({ open, lead, agents = [], onClose, onReassigned }) {
  const [agentId, setAgentId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAgentId(""); setNote(""); }
  }, [open, lead?.id]);

  if (!open || !lead) return null;

  const leadName = lead.name || lead.full_name || lead.email || "this lead";

  const submit = async () => {
    if (!agentId || saving) return;
    setSaving(true);
    try {
      await reassignIgnoredLead(lead.id, agentId, note);
      const agentName = agents.find((a) => String(a.id) === String(agentId))?.name || "the agent";
      toast.success(`Lead re-assigned to ${agentName} and restored to their pipeline.`);
      onReassigned && onReassigned(lead);
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not re-assign the lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-[#55C5D0]" />
            Re-assign ignored lead
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500">
            Re-assigning <span className="font-semibold text-gray-700">{leadName}</span> un-ignores it and
            puts it back into the chosen agent's pipeline. They'll get the usual assignment alert.
          </p>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Assign to</label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            >
              <option value="">Choose an agent…</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Note to the agent <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Give them context on why you're sending this over."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={submit}
            disabled={!agentId || saving}
            className="px-4 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold hover:bg-[#45b5c0] disabled:opacity-50 flex items-center gap-1.5"
          >
            <UserCheck className="w-4 h-4" /> {saving ? "Re-assigning…" : "Re-assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
